// The one-line fact shown where a spend is chosen:
//   "Uses 600 credits · you have 1,000"
//   "This needs 500 credits; you have 350."   (a tap would be blocked)
//   "Uses 600 credits · you have —"           (balance unknown after a failed fetch)
//   "Uses 600 credits"                        (balance not applicable — a guest)
// Never a warning colour, never urgency: it is information.

import styled from "styled-components";
import { formatCredits, formatCreditsNumber } from "../../constants/credits";

const Line = styled.p`
  font-size: 14px;
  line-height: 1.5;
  color: var(--brand-ink-2, #2b3f42);
  font-weight: ${(p) => (p.$variant === "blocked" ? 600 : 500)};
  margin: 0;
`;

/**
 * @param {number} cost
 * @param {number | null | undefined} balance
 *   null = unknown (show "—"); undefined = not applicable (omit the clause).
 * @param {boolean} [blocked] Force the blocked phrasing (e.g. from a server 409).
 */
export const costLineText = (cost, balance, blocked) => {
  if (balance === undefined) return `Uses ${formatCredits(cost)}`;
  if (balance === null) return `Uses ${formatCredits(cost)} · you have —`;
  if (blocked ?? cost > balance) {
    return `This needs ${formatCredits(cost)}; you have ${formatCreditsNumber(balance)}.`;
  }
  return `Uses ${formatCredits(cost)} · you have ${formatCreditsNumber(balance)}`;
};

/**
 * @param {{
 *   cost: number,
 *   balance?: number | null,
 *   blocked?: boolean,
 *   className?: string,
 * }} props
 */
const CostLine = ({ cost, balance, blocked, className }) => {
  const isBlocked =
    blocked ?? (typeof balance === "number" && Number.isFinite(balance) && cost > balance);
  return (
    <Line
      $variant={isBlocked ? "blocked" : "default"}
      className={className}
      data-testid="cost-line"
    >
      {costLineText(cost, balance, blocked)}
    </Line>
  );
};

export default CostLine;
